import { create } from 'zustand'
import { config } from '../config'

export type CameraStatus =
  | 'idle' // 还未请求
  | 'requesting' // getUserMedia 进行中
  | 'granted' // 已授权且 stream 在跑
  | 'declined' // 用户主动选择键鼠模式（静默关闭对话框）
  | 'denied' // 用户拒绝了浏览器权限弹窗
  | 'unsupported' // 浏览器不支持 getUserMedia / 没有摄像头
  | 'error' // 其他错误（设备占用等）

type CameraStoreState = {
  status: CameraStatus
  stream: MediaStream | null
  videoEl: HTMLVideoElement | null
  errorMessage: string | null
  request: () => Promise<void>
  decline: () => void
  stop: () => void
}

let _video: HTMLVideoElement | null = null
function ensureVideoEl() {
  if (_video) return _video
  const v = document.createElement('video')
  v.autoplay = true
  v.playsInline = true
  v.muted = true
  v.style.position = 'absolute'
  v.style.width = '1px'
  v.style.height = '1px'
  v.style.opacity = '0'
  v.style.pointerEvents = 'none'
  v.style.left = '-9999px'
  v.style.top = '-9999px'
  document.body.appendChild(v)
  _video = v
  return v
}

export const useCameraStore = create<CameraStoreState>((set, get) => ({
  status: 'idle',
  stream: null,
  videoEl: null,
  errorMessage: null,
  request: async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      set({ status: 'unsupported', errorMessage: '此浏览器不支持摄像头 API' })
      return
    }
    const current = get().status
    if (current === 'requesting' || current === 'granted') return
    set({ status: 'requesting', errorMessage: null })
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          width: { ideal: config.tracking.videoWidth },
          height: { ideal: config.tracking.videoHeight },
          facingMode: 'user',
        },
      })
      const v = ensureVideoEl()
      v.srcObject = stream
      await v.play().catch(() => {
        /* autoplay 可能被阻止，但 HAVE_ENOUGH_DATA 仍会触发 */
      })
      set({ status: 'granted', stream, videoEl: v, errorMessage: null })
    } catch (err: any) {
      const name = err?.name
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        set({ status: 'denied', errorMessage: '用户拒绝了摄像头权限' })
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        set({ status: 'unsupported', errorMessage: '未找到可用摄像头' })
      } else {
        set({ status: 'error', errorMessage: err?.message ?? String(err) })
      }
    }
  },
  decline: () => set({ status: 'declined', errorMessage: null }),
  stop: () => {
    const s = get().stream
    if (s) s.getTracks().forEach((t) => t.stop())
    if (_video) {
      _video.srcObject = null
    }
    set({ status: 'idle', stream: null, videoEl: null })
  },
}))
